// src/pages/Communities.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiPlusCircle, FiUsers, FiMapPin, FiStar } from 'react-icons/fi';
import { useAuth } from '../services/auth';
import { getUserCreatedCommunities, getUserJoinedCommunities } from '../services/communityService';
import '../styles/Communities.scss';

const CommunityCard = ({ community, isOwner }) => {
  const navigate = useNavigate();

  const getImageSource = () => {
    return community.cover_image_cdn_url || community.cover_image_direct_url || '/images/communities/default.jpg';
  };

  return (
    <motion.div
      className="community-card"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      onClick={() => navigate(`/${community.username}`)}
    >
      <div className="community-image-container">
        <img src={getImageSource()} alt={community.name} className="community-image" />
        <div className="community-gradient" />
      </div>

      <div className="community-badges">
        {isOwner && (
          <div className="badge owner-badge">Owner</div>
        )}
        {community.membership_enabled && (
          <div className="badge membership-badge">
            <FiStar size={12} />
            Membership
          </div>
        )}
      </div>

      <div className="community-content">
        <h3 className="community-name">{community.name}</h3>

        <div className="community-detail">
          <FiUsers size={14} />
          <span>{community.members_count} {community.members_count === 1 ? 'member' : 'members'}</span>
        </div>

        {community.city && (
          <div className="community-detail">
            <FiMapPin size={14} />
            <span>{community.city}, {community.state}</span>
          </div>
        )}

        {community.type && (
          <div className="community-type-tag">{community.type}</div>
        )}
      </div>
    </motion.div>
  );
};

const EmptyState = ({ activeTab, navigate }) => (
  <div className="empty-state">
    <FiUsers size={60} />
    <h2>{activeTab === 'created' ? 'No communities created' : 'No communities joined'}</h2>
    <p>
      {activeTab === 'created'
        ? "You haven't created any communities yet"
        : "You haven't joined any communities yet"}
    </p>
    {activeTab === 'created' && (
      <button className="create-community-btn" onClick={() => navigate('/create-community')}>
        <FiPlusCircle size={20} />
        Create Community
      </button>
    )}
  </div>
);

const Communities = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [activeTab, setActiveTab] = useState('created');
  const [loading, setLoading] = useState(true);
  const [createdCommunities, setCreatedCommunities] = useState([]);
  const [joinedCommunities, setJoinedCommunities] = useState([]);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
        return;
      }
      fetchCommunities();
    }
  }, [activeTab, authLoading, user, navigate]);

  const fetchCommunities = async () => {
    try {
      setLoading(true);

      if (activeTab === 'created') {
        const data = await getUserCreatedCommunities();
        setCreatedCommunities(data);
      } else {
        const data = await getUserJoinedCommunities();
        setJoinedCommunities(data);
      }
    } catch (error) {
      console.error('Error fetching communities:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading communities...</p>
      </div>
    );
  }

  const currentCommunities = activeTab === 'created' ? createdCommunities : joinedCommunities;

  return (
    <div className="communities-page">
      {/* Background */}
      <div className="communities-background">
        <img src="/images/backgrounds/3.jpg" alt="" />
        <div className="background-overlay" />
      </div>

      {/* Header */}
      <motion.div
        className="communities-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1>Communities</h1>
        <div className="header-actions">
          <button
            className="header-btn"
            onClick={() => navigate('/search-communities')}
            title="Search Communities"
          >
            <FiSearch size={24} />
          </button>
          <button
            className="header-btn"
            onClick={() => navigate('/create-community')}
            title="Create Community"
          >
            <FiPlusCircle size={24} />
          </button>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        className="communities-tabs"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        <button
          className={`tab ${activeTab === 'created' ? 'active' : ''}`}
          onClick={() => setActiveTab('created')}
        >
          Created
        </button>
        <button
          className={`tab ${activeTab === 'joined' ? 'active' : ''}`}
          onClick={() => setActiveTab('joined')}
        >
          Joined
        </button>
      </motion.div>

      {/* Content */}
      <div className="communities-content">
        {currentCommunities.length === 0 ? (
          <EmptyState activeTab={activeTab} navigate={navigate} />
        ) : (
          <motion.div
            className="communities-grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {currentCommunities.map((community, index) => (
              <motion.div
                key={community.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <CommunityCard
                  community={community}
                  isOwner={activeTab === 'created'}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Communities;
